/**
 * MLSGroupManager — High-level wrapper around ts-mls (RFC 9420).
 *
 * Provides a stateful, ergonomic API for MLS group operations:
 * create, join, encrypt, decrypt, add/remove members, state serialization.
 *
 * Default cipher suite: MLS_128_DHKEMX25519_AES128GCM_SHA256_Ed25519
 */

import {
  // Key package generation
  generateKeyPackage,
  type KeyPackage,
  type PrivateKeyPackage,

  // Group lifecycle
  createGroup,
  joinGroup,
  type ClientState,

  // Commit / proposals
  createCommit,
  type CreateCommitResult,
  type CreateCommitOptions,

  // Application messages
  createApplicationMessage,

  // Message processing
  processMessage,
  type ProcessMessageResult,

  // Message encoding
  encodeMlsMessage,
  decodeMlsMessage,
  type MLSMessage,

  // Cipher suite
  getCiphersuiteFromName,
  getCiphersuiteImpl,
  type CiphersuiteImpl,
  type CiphersuiteName,

  // Crypto providers
  nobleCryptoProvider,
  type CryptoProvider,

  // Defaults
  defaultCapabilities,
  type Capabilities,
  defaultLifetime,
  defaultKeyRetentionConfig,
  defaultLifetimeConfig,
  defaultKeyPackageEqualityConfig,
  defaultPaddingConfig,
  defaultAuthenticationService,
  emptyPskIndex,
  acceptAll,

  // State serialization
  encodeGroupState,
  decodeGroupState,

  // Credential
  type Credential,

  // PSK
  type PskIndex,

  // Proposals
  type Proposal,
  type ClientConfig,
} from "ts-mls";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Default cipher suite: X25519 + AES-128-GCM + SHA-256 + Ed25519 */
const DEFAULT_CIPHERSUITE: CiphersuiteName =
  "MLS_128_DHKEMX25519_AES128GCM_SHA256_Ed25519";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A key package bundle with both public and private components. */
export interface MLSKeyPackageBundle {
  publicPackage: KeyPackage;
  privatePackage: PrivateKeyPackage;
}

/** Result of an add-members commit. */
export interface AddMembersResult {
  /** The encoded commit message to broadcast to existing members. */
  commit: Uint8Array;
  /** The encoded welcome message to send to new members. */
  welcome: Uint8Array | undefined;
}

/** Result of `reAdmitMembers`: commits to broadcast IN ORDER (#1093). */
export interface ReAdmitMembersResult {
  /**
   * One commit for pure adds; two (removes, then adds) when a leaf is stale.
   * Each carries the group epoch it PRODUCED, because the caller's
   * confirmation gate has to ask the server whether that exact epoch landed —
   * and after two commits the manager only knows the last one.
   */
  commits: Array<{ commit: Uint8Array; epoch: bigint }>;
  /** Delivered to EVERY member in the batch — each finds its own secrets. */
  welcome?: Uint8Array;
}

/** Result of a remove-member commit. */
export interface RemoveMemberResult {
  /** The encoded commit message to broadcast. */
  commit: Uint8Array;
}

/** Result of decrypting an incoming message. */
export interface DecryptResult {
  /** The decrypted plaintext, or undefined if this was a handshake message. */
  plaintext?: Uint8Array;
  /** Whether the message was a handshake (commit/proposal) vs application data. */
  isHandshake: boolean;
}

/** Serialized group state for persistence. */
export interface SerializedMLSGroupState {
  /** TLS-encoded group state bytes, base64. */
  groupState: string;
  /** The cipher suite name used by this group. */
  ciphersuite: CiphersuiteName;
}

/** Constant-shape byte-array equality (length + element-wise). */
function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/**
 * The basic-credential identity bytes of a KeyPackage, or `null` when the
 * KeyPackage carries a non-basic credential (whose identity we can't compare,
 * so it is never treated as a duplicate). Mirrors the leaf-credential read in
 * {@link MLSGroupManager.memberLeafIndex}.
 */
function keyPackageIdentity(kp: KeyPackage): Uint8Array | null {
  const cred = kp.leafNode?.credential;
  if (cred && cred.credentialType === "basic") {
    return cred.identity;
  }
  return null;
}

// ---------------------------------------------------------------------------
// MLSGroupManager
// ---------------------------------------------------------------------------

export class MLSGroupManager {
  /** @internal */ state: ClientState | undefined;
  private _cs: CiphersuiteImpl | undefined;
  private _ciphersuiteName: CiphersuiteName;
  private _pskIndex: PskIndex;
  private _provider: CryptoProvider;

  constructor(ciphersuite?: CiphersuiteName, provider?: CryptoProvider) {
    this._ciphersuiteName = ciphersuite ?? DEFAULT_CIPHERSUITE;
    this._pskIndex = emptyPskIndex;
    this._provider = provider ?? nobleCryptoProvider;
  }

  // -------------------------------------------------------------------------
  // Getters
  // -------------------------------------------------------------------------

  /** Whether the group has been initialized (created or joined). */
  get isInitialized(): boolean {
    return this.state !== undefined;
  }

  /** Current epoch number. Throws if not initialized. */
  get epoch(): bigint {
    const s = this._requireState();
    return s.groupContext.epoch;
  }

  /** Group identifier as Uint8Array. Throws if not initialized. */
  get groupId(): Uint8Array {
    const s = this._requireState();
    return s.groupContext.groupId;
  }

  /**
   * Proof that this client actually holds the group's keys at its current
   * epoch. Throws if not initialized.
   *
   * RFC 9420 §8.2's `epoch_authenticator`, derived from the epoch secret. Two
   * properties make it the thing this codebase has been missing:
   *
   *   * every member at the same epoch computes the SAME value, so it is
   *     comparable across clients and can be checked by a server;
   *   * it cannot be computed without the key schedule, so possessing it is
   *     evidence of membership rather than a claim about it. RFC 9420 names
   *     this as its purpose: "to authenticate the shared group state".
   *
   * It is safe to publish. It is a KDF output over the epoch secret, not the
   * secret, and the RFC intends it to be shown to out-of-band verifiers.
   *
   * WHY THIS EXISTS. `mls_groups.member_device_ids` records membership; the
   * ability to decrypt lives only in one browser's localStorage. Nothing
   * compared the two, and nothing could — so a device in the roster holding no
   * keys was indistinguishable from a healthy one, on every signal the server
   * has. Measured on production 2026-08-26: one owner device carried the keys
   * for 19 of the 21 owner leaves on the platform, and one 1:1 group had been
   * unreadable by its owner's browser for two months while every status column
   * read healthy.
   *
   * `epoch` alone cannot do this job: it is a number the client reports about
   * itself, it collides across groups, and a client that has lost its state
   * still remembers one.
   */
  get epochAuthenticator(): Uint8Array {
    const s = this._requireState();
    return s.keySchedule.epochAuthenticator;
  }

  /** The cipher suite name in use. */
  get ciphersuiteName(): CiphersuiteName {
    return this._ciphersuiteName;
  }

  // -------------------------------------------------------------------------
  // Key Package Generation
  // -------------------------------------------------------------------------

  /**
   * Generate a key package for joining or creating a group.
   *
   * @param identity - The identity bytes for the basic credential (e.g. UTF-8 user ID).
   * @param capabilities - Optional override for the leaf node capabilities.
   *   Defaults to ts-mls's `defaultCapabilities()` (greased) so existing
   *   callers see no behavior change. WS-23 fixture generation passes a
   *   non-greased Capabilities object here for byte-stable output.
   * @returns A bundle containing both public and private key package components.
   */
  async generateKeyPackage(
    identity: Uint8Array,
    capabilities?: Capabilities,
  ): Promise<MLSKeyPackageBundle> {
    const cs = await this._ensureCiphersuite();
    const credential: Credential = {
      credentialType: "basic",
      identity,
    };
    const { publicPackage, privatePackage } = await generateKeyPackage(
      credential,
      capabilities ?? defaultCapabilities(),
      defaultLifetime,
      [], // extensions
      cs,
    );
    return { publicPackage, privatePackage };
  }

  /**
   * Serialize a KeyPackage to bytes for transport (e.g. publishing to backend).
   * Wraps in an MLS message envelope with wireformat mls_key_package.
   */
  static serializeKeyPackage(kp: KeyPackage): Uint8Array {
    const msg: MLSMessage = {
      version: "mls10",
      wireformat: "mls_key_package",
      keyPackage: kp,
    };
    return encodeMlsMessage(msg);
  }

  /**
   * Deserialize a KeyPackage from bytes (e.g. fetched from backend).
   * Expects bytes produced by serializeKeyPackage (MLS message envelope).
   */
  static deserializeKeyPackage(bytes: Uint8Array): KeyPackage {
    const result = decodeMlsMessage(bytes, 0);
    if (!result) {
      throw new Error("Failed to decode MLS key package message");
    }
    const [msg] = result;
    if (msg.wireformat !== "mls_key_package") {
      throw new Error(`Expected mls_key_package wireformat, got ${msg.wireformat}`);
    }
    return (msg as unknown as { keyPackage: KeyPackage }).keyPackage;
  }

  // -------------------------------------------------------------------------
  // Group Lifecycle
  // -------------------------------------------------------------------------

  /**
   * Create a new MLS group. The caller becomes the first (and only) member.
   *
   * @param groupId - Unique group identifier bytes.
   * @param keyPackageBundle - The creator's key package bundle.
   */
  async createGroup(
    groupId: Uint8Array,
    keyPackageBundle: MLSKeyPackageBundle,
  ): Promise<void> {
    const cs = await this._ensureCiphersuite();
    this.state = await createGroup(
      groupId,
      keyPackageBundle.publicPackage,
      keyPackageBundle.privatePackage,
      [], // extensions
      cs,
    );
  }

  /**
   * Join an existing group from a welcome message.
   *
   * @param welcomeBytes - The TLS-encoded welcome message bytes.
   * @param keyPackageBundle - The joiner's key package bundle (must match the
   *   key package that was added by the group creator).
   */
  async joinFromWelcome(
    welcomeBytes: Uint8Array,
    keyPackageBundle: MLSKeyPackageBundle,
  ): Promise<void> {
    const cs = await this._ensureCiphersuite();
    // Decode the MLS message to extract the Welcome
    const mlsMsg = decodeMlsMessage(welcomeBytes, 0);
    if (!mlsMsg) {
      throw new Error("Failed to decode MLS welcome message");
    }
    const [decoded] = mlsMsg;
    if (decoded.wireformat !== "mls_welcome") {
      throw new Error(
        `Expected mls_welcome wireformat, got ${decoded.wireformat}`,
      );
    }
    this.state = await joinGroup(
      decoded.welcome,
      keyPackageBundle.publicPackage,
      keyPackageBundle.privatePackage,
      this._pskIndex,
      cs,
    );
  }

  // -------------------------------------------------------------------------
  // Membership Management
  // -------------------------------------------------------------------------

  /**
   * Add one or more members to the group.
   *
   * @param keyPackages - The public key packages of members to add.
   * @returns The commit and welcome messages to distribute.
   */
  async addMembers(keyPackages: KeyPackage[]): Promise<AddMembersResult> {
    const s = this._requireState();
    const cs = await this._ensureCiphersuite();

    // Dedupe by basic-credential identity before committing. ts-mls does NOT
    // reject an add whose identity already has a leaf when the KeyPackage
    // differs — e.g. a second KeyPackage published by the same device under the
    // backend concurrent-republish race, or a not-yet-joined member re-added by
    // the missing-member sweep. A blind add then mints a DUPLICATE leaf that the
    // joiner (mls-rs) rejects with "duplicate signature key, hpke key or
    // identity found at index N", permanently blocking the join (observed live
    // in fresh rooms, 2026-06-18). Drop any KeyPackage whose identity is already
    // in the tree or repeated within this batch; KeyPackages whose identity we
    // can't read (non-basic credential) are always kept. If nothing remains,
    // throw "already in the group" — the exact signal the re-admit recovery in
    // mls-welcome-fulfillment keys on (and what ts-mls already throws for an
    // identical-KeyPackage re-add).
    const seenIdentities: Uint8Array[] = [];
    const toAdd: KeyPackage[] = [];
    for (const kp of keyPackages) {
      const identity = keyPackageIdentity(kp);
      if (identity !== null) {
        const inTree = this.memberLeafIndex(identity) !== null;
        const inBatch = seenIdentities.some((id) => bytesEqual(id, identity));
        if (inTree || inBatch) continue;
        seenIdentities.push(identity);
      }
      toAdd.push(kp);
    }
    if (toAdd.length === 0) {
      throw new Error("already in the group");
    }

    // Build add proposals — ts-mls ProposalAdd shape: { proposalType: "add", add: { keyPackage } }
    const extraProposals: Proposal[] = toAdd.map((kp) => ({
      proposalType: "add" as const,
      add: { keyPackage: kp },
    }));

    const options: CreateCommitOptions = {
      extraProposals,
      ratchetTreeExtension: true,
    };

    const result: CreateCommitResult = await createCommit(
      { state: s, cipherSuite: cs, pskIndex: this._pskIndex },
      options,
    );

    this.state = result.newState;

    // Encode commit
    const commitBytes = encodeMlsMessage(result.commit);

    // Encode welcome if present
    let welcomeBytes: Uint8Array | undefined;
    if (result.welcome) {
      const welcomeMsg: MLSMessage = {
        version: "mls10",
        wireformat: "mls_welcome",
        welcome: result.welcome,
      };
      welcomeBytes = encodeMlsMessage(welcomeMsg);
    }

    return { commit: commitBytes, welcome: welcomeBytes };
  }

  /**
   * Admit N members with TWO commits total, replacing any stale leaf they hold.
   *
   * `addMembers` cannot do this alone: MLS rejects an Add for an identity
   * already in the tree, so a member that lost its local state must be removed
   * first. Doing that pair PER MEMBER is what the fulfiller did, and it is not
   * merely slow — it is self-defeating.
   *
   * Live 2026-08-28 14:02:21-28Z: the owner sweep re-admitted 7 room members
   * sequentially, each as its own removeMember + addMembers. Room 007 ran
   * **epoch 71 -> 85 in seven seconds**, and the Welcome minted for member n
   * named an epoch that member n+1's commit had already replaced — so six of
   * seven were stale before they finished joining, and one (`smartcode`) could
   * not replay forward afterwards at all.
   *
   * 🎯 A REPAIR THAT MOVES THE EPOCH ONCE PER MEMBER IS RACING ITSELF.
   *
   * ⚠️ **Why two commits and not one.** RFC 9420 §12.3 allows Remove and Add in
   * a single commit, and ts-mls builds it happily — but with MORE THAN ONE of
   * each, a REMAINING member fails to process it:
   *
   *     OpenError  at applyUpdatePathSecret (ts-mls/src/createCommit.ts:471)
   *                at updatePrivateKeyPath  (processMessages.ts:397)
   *
   * One remove + one add in a single commit is fine; 2+2 is not. That is
   * pinned by a test in `mls-group.test.ts` so this cannot be "optimised" back
   * into one commit without the failure showing up first. Two commits still
   * turns 7 members from 14 epochs into 2, and every joiner lands current.
   *
   * @param keyPackages - Fresh KeyPackages, one per member to admit. Members
   *   already holding a leaf are removed by the first commit; members with no
   *   leaf are plain adds. Duplicate identities keep the first KeyPackage.
   * @returns Every commit to broadcast IN ORDER, and the Welcome to deliver to
   *   EVERY member in the batch (each finds its own secrets inside it).
   */
  async reAdmitMembers(keyPackages: KeyPackage[]): Promise<ReAdmitMembersResult> {
    this._requireState();
    await this._ensureCiphersuite();

    const seenIdentities: Uint8Array[] = [];
    const toAdd: KeyPackage[] = [];
    const leavesToRemove: number[] = [];
    for (const kp of keyPackages) {
      const identity = keyPackageIdentity(kp);
      if (identity !== null) {
        if (seenIdentities.some((id) => bytesEqual(id, identity))) continue;
        seenIdentities.push(identity);
        // Unlike `addMembers`, an identity already in the tree is NOT dropped
        // here — it is the whole point. It is removed first so the Add lands
        // on a fresh leaf.
        const leaf = this.memberLeafIndex(identity);
        if (leaf !== null) leavesToRemove.push(leaf);
      }
      toAdd.push(kp);
    }
    if (toAdd.length === 0) {
      throw new Error("reAdmitMembers: no members to admit");
    }

    const commits: Array<{ commit: Uint8Array; epoch: bigint }> = [];
    if (leavesToRemove.length > 0) {
      const { commit } = await this.removeMembers(leavesToRemove);
      commits.push({ commit, epoch: this.epoch });
    }
    const added = await this.addMembers(toAdd);
    commits.push({ commit: added.commit, epoch: this.epoch });
    return { commits, welcome: added.welcome };
  }

  async removeMembers(memberIndices: number[]): Promise<RemoveMemberResult> {
    const s = this._requireState();
    const cs = await this._ensureCiphersuite();
    if (memberIndices.length === 0) {
      throw new Error("removeMembers: no members to remove");
    }
    const extraProposals: Proposal[] = memberIndices.map((removed) => ({
      proposalType: "remove" as const,
      remove: { removed },
    }));
    const result = await createCommit(
      { state: s, cipherSuite: cs, pskIndex: this._pskIndex },
      { extraProposals },
    );
    this.state = result.newState;
    return { commit: encodeMlsMessage(result.commit) };
  }

  async removeMember(memberIndex: number): Promise<RemoveMemberResult> {
    const s = this._requireState();
    const cs = await this._ensureCiphersuite();

    // ts-mls ProposalRemove shape: { proposalType: "remove", remove: { removed: number } }
    const removeProposal: Proposal = {
      proposalType: "remove" as const,
      remove: { removed: memberIndex },
    };

    const options: CreateCommitOptions = {
      extraProposals: [removeProposal],
    };

    const result = await createCommit(
      { state: s, cipherSuite: cs, pskIndex: this._pskIndex },
      options,
    );

    this.state = result.newState;
    return { commit: encodeMlsMessage(result.commit) };
  }

  /**
   * Find the leaf index of a member by its basic-credential identity, or
   * `null` if no member in the current group state has that identity.
   *
   * Drives re-admit recovery: when a member is in the server roster but has
   * lost its local MLS state, `addMembers` rejects it ("already in the
   * group"). The fulfiller looks up its leaf index here, `removeMember`s it,
   * then re-adds it with a fresh KeyPackage so it gets a Welcome at the
   * current epoch it can actually apply.
   */
  memberLeafIndex(identity: Uint8Array): number | null {
    const s = this._requireState();
    const tree = s.ratchetTree;
    // Leaf nodes occupy even node indices; leaf index = nodeIndex / 2.
    for (let nodeIndex = 0; nodeIndex < tree.length; nodeIndex += 2) {
      const node = tree[nodeIndex];
      if (node && node.nodeType === "leaf") {
        const cred = node.leaf.credential;
        if (
          cred.credentialType === "basic" &&
          cred.identity.length === identity.length &&
          cred.identity.every((b, i) => b === identity[i])
        ) {
          return nodeIndex / 2;
        }
      }
    }
    return null;
  }

  // -------------------------------------------------------------------------
  // Encrypt / Decrypt
  // -------------------------------------------------------------------------

  /**
   * Encrypt an application message for the group.
   *
   * @param plaintext - The plaintext bytes to encrypt.
   * @returns The TLS-encoded private message bytes.
   */
  async encrypt(plaintext: Uint8Array): Promise<Uint8Array> {
    const s = this._requireState();
    const cs = await this._ensureCiphersuite();

    const { newState, privateMessage } = await createApplicationMessage(
      s,
      plaintext,
      cs,
    );

    this.state = newState;

    // Encode as MLS private message
    const mlsMsg: MLSMessage = {
      version: "mls10",
      wireformat: "mls_private_message",
      privateMessage,
    };
    return encodeMlsMessage(mlsMsg);
  }

  /**
   * Decrypt / process an incoming MLS message (application or handshake).
   *
   * @param messageBytes - The TLS-encoded MLS message bytes.
   * @returns The decrypted plaintext (for application messages) or handshake info.
   */
  async decrypt(messageBytes: Uint8Array): Promise<DecryptResult> {
    const s = this._requireState();
    const cs = await this._ensureCiphersuite();

    const decoded = decodeMlsMessage(messageBytes, 0);
    if (!decoded) {
      throw new Error("Failed to decode MLS message");
    }
    const [mlsMsg] = decoded;

    if (
      mlsMsg.wireformat !== "mls_private_message" &&
      mlsMsg.wireformat !== "mls_public_message"
    ) {
      throw new Error(
        `Cannot decrypt message of wireformat: ${mlsMsg.wireformat}`,
      );
    }

    const result: ProcessMessageResult = await processMessage(
      mlsMsg,
      s,
      this._pskIndex,
      acceptAll,
      cs,
    );

    this.state = result.newState;

    if (result.kind === "applicationMessage") {
      return { plaintext: result.message, isHandshake: false };
    }

    return { isHandshake: true };
  }

  // -------------------------------------------------------------------------
  // Commit Processing
  // -------------------------------------------------------------------------

  /**
   * Process an incoming commit message (e.g. from addMembers or removeMember
   * by another group member). Use `decrypt()` for general message processing;
   * this is an alias that makes intent clearer for handshake messages.
   *
   * @param commitBytes - The TLS-encoded commit message bytes.
   */
  async processCommit(commitBytes: Uint8Array): Promise<void> {
    const result = await this.decrypt(commitBytes);
    if (!result.isHandshake) {
      throw new Error("Expected a handshake message but got application data");
    }
  }

  // -------------------------------------------------------------------------
  // State Serialization
  // -------------------------------------------------------------------------

  /**
   * Export the current group state for persistence.
   * The serialized state includes everything needed to resume operations.
   */
  exportState(): SerializedMLSGroupState {
    const s = this._requireState();
    const encoded = encodeGroupState(s);
    return {
      groupState: uint8ArrayToBase64(encoded),
      ciphersuite: this._ciphersuiteName,
    };
  }

  /**
   * Import a previously exported group state.
   *
   * @param serialized - The serialized state from `exportState()`.
   */
  importState(serialized: SerializedMLSGroupState): void {
    this._ciphersuiteName = serialized.ciphersuite;
    // Reset cached cipher suite impl so it gets re-created for the right suite
    this._cs = undefined;

    const bytes = base64ToUint8Array(serialized.groupState);
    const decoded = decodeGroupState(bytes, 0);
    if (!decoded) {
      throw new Error("Failed to decode MLS group state");
    }
    const [groupState] = decoded;
    // Reconstruct ClientState from GroupState with default client config
    const clientConfig: ClientConfig = {
      keyRetentionConfig: defaultKeyRetentionConfig,
      lifetimeConfig: defaultLifetimeConfig,
      keyPackageEqualityConfig: defaultKeyPackageEqualityConfig,
      paddingConfig: defaultPaddingConfig,
      authService: defaultAuthenticationService,
    };
    this.state = {
      ...groupState,
      clientConfig,
    };
  }

  // -------------------------------------------------------------------------
  // Private Helpers
  // -------------------------------------------------------------------------

  private _requireState(): ClientState {
    if (!this.state) {
      throw new Error("MLSGroupManager is not initialized. Call createGroup() or joinFromWelcome() first.");
    }
    return this.state;
  }

  private async _ensureCiphersuite(): Promise<CiphersuiteImpl> {
    if (!this._cs) {
      const suite = getCiphersuiteFromName(this._ciphersuiteName);
      this._cs = await getCiphersuiteImpl(suite, this._provider);
    }
    return this._cs;
  }
}

// ---------------------------------------------------------------------------
// Utility: Base64 <-> Uint8Array (no dependency on Node Buffer)
// ---------------------------------------------------------------------------

function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToUint8Array(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
